import { UseFormReturn } from "react-hook-form";
import { RouteRequestFormData } from "./TripFormTypeConversions";
import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Tooltip } from "@mui/material";
import {
    DELETE_CONFIRMATION_NO,
    LENGTH_OF_STAY_OVERNIGHT,
    LENGTH_OF_STAY_PITSTOP,
    LENGTH_OF_STAY_TOOLTIP,
    LENGTH_OF_STAY_TOOLTIP_PITSTOP,
    OVERNIGHT_STOP,
    PIT_STOP_STRING,
    VALUE_NUMERIC
} from "../../UserVisibleStrings";
import React, { useEffect, useState } from "react";
import { updateWaypoint, useRiskViewerState } from "../../../features/riskViewer/riskViewerSlice";
import { useDispatch } from "react-redux";
import { Waypoint } from "../../../generated/gpb/api/route";
import Toggle from "./Toggle";
import { validateLengthOfStay } from "./validation";

type LengthOfStayDialogProps = {
    open: boolean,
    handleClose: () => void,
    formParent: UseFormReturn<RouteRequestFormData>,
    index: number,
    isIntermediate: boolean,
    isAdvisory: boolean,
};

export function LengthOfStayDialog( {
    open,
    handleClose,
    formParent,
    index,
    isIntermediate,
    isAdvisory,
}: LengthOfStayDialogProps )
{
    const storedValuePitStop = formParent.getValues().waypoints[index]?.pitStop?.minDurationMinutes !== undefined ? formParent.getValues().waypoints[index]?.pitStop?.minDurationMinutes : undefined;
    const storedValueOvernightStay = formParent.getValues().waypoints[index]?.overnightStop?.minNights !== undefined ? formParent.getValues().waypoints[index]?.overnightStop?.minNights : undefined;
    const stateWaypoints = useRiskViewerState().waypoints;
    const dispatch = useDispatch();
    const defaultValueOvernightStay = storedValueOvernightStay !== undefined ? storedValueOvernightStay : 1;
    const defaultValuePitStop = storedValuePitStop !== undefined ? storedValuePitStop : 0;
    const defaultIsPitStop = storedValueOvernightStay === undefined && storedValuePitStop !== undefined;
    const [ isPitStop, setIsPitStop ] = useState( defaultIsPitStop );
    const [ shouldDisableSave, setShouldDisableSave ] = useState( false );
    const [ invalidInputMessage, setInvalidInputMessage ] = useState<string | undefined>( undefined );
    const [ lengthOfStay, setLengthOfStay ] = useState( defaultIsPitStop ? defaultValuePitStop : defaultValueOvernightStay );

    useEffect( () =>
    {
        const errorMessage = validateLengthOfStay( {
            lengthOfStay,
            isPitStop,
            isAdvisory,
            dailyStartTime: formParent.getValues( "dailyStartTime" ),
            dailyFinalEndTime: formParent.getValues( "dailyFinalEndTime" ),
        } );

        if ( errorMessage !== undefined )
        {
            setShouldDisableSave( true );
            setInvalidInputMessage( errorMessage );
        }
        else
        {
            setShouldDisableSave( false );
            setInvalidInputMessage( undefined );
        }
    }, [ lengthOfStay, isPitStop, isAdvisory ] );


    const cancelAndClose = () =>
    {
        const newVal = storedValuePitStop ?? storedValueOvernightStay ?? 0;
        setLengthOfStay( newVal );
        setIsPitStop( defaultIsPitStop );
        handleClose();
    };
    const saveAndClose = () =>
    {
        if ( isPitStop )
        {
            formParent.setValue( `waypoints.${index}.overnightStop`, undefined );
            formParent.setValue( `waypoints.${index}.pitStop.minDurationMinutes`, lengthOfStay );
            formParent.setValue( `waypoints.${index}.pitStop.maxDurationMinutes`, lengthOfStay );
            updateWaypointState( lengthOfStay, lengthOfStay, "pitStop" );
        }
        else
        {
            formParent.setValue( `waypoints.${index}.pitStop`, undefined );
            formParent.setValue( `waypoints.${index}.overnightStop.minNights`, lengthOfStay );
            formParent.setValue( `waypoints.${index}.overnightStop.maxNights`, lengthOfStay );
            updateWaypointState( lengthOfStay, lengthOfStay, "overnightStop" );
        }
        handleClose();
    };
    const updateWaypointState = ( minDuration: number, maxDuration: number, type: "pitStop" | "overnightStop" ) =>
    {
        if ( stateWaypoints !== undefined && stateWaypoints.length > index )
        {
            const updatedWaypoint = {
                ...stateWaypoints[index],
                [type]: {
                    minDurationMinutes: type === "pitStop" ? minDuration : undefined,
                    maxDurationMinutes: type === "pitStop" ? maxDuration : undefined,
                    minNights: type === "overnightStop" ? minDuration : undefined,
                    maxNights: type === "overnightStop" ? maxDuration : undefined,
                },
                [type === "pitStop" ? "overnightStop" : "pitStop"]: undefined,
            } as Waypoint;

            dispatch( updateWaypoint( {
                index,
                waypoint: updatedWaypoint,
                isDrag: false,
            } ) );
        }
    };
    const setNumericValue = ( e: React.ChangeEvent<HTMLInputElement> ) =>
    {
        const input = e.target.value ?? "";
        const inputNumber = Number( input );
        if ( !isNaN( inputNumber ) )
        {
            setLengthOfStay( inputNumber );
            setShouldDisableSave( false );
        }
        else
        {
            setShouldDisableSave( true );
            setInvalidInputMessage( VALUE_NUMERIC );
        }
    };

    return (
        <Dialog open = {open} onClose = {handleClose} PaperProps = {{ style: { width: "350px" } }}>
            <DialogTitle>
                {isIntermediate && (
                    <div style = {{ display: "flex", flexDirection: "column" }}>
                        <Toggle
                            value = {isPitStop}
                            setValue = {setIsPitStop}
                            labelFalse = {OVERNIGHT_STOP}
                            labelTrue = {PIT_STOP_STRING}
                        />
                    </div>
                )}
            </DialogTitle>
            {isIntermediate && (
                <DialogContent sx = {{ overflowY: "visible" }}>
                    {isPitStop ? (
                        <Tooltip title = {LENGTH_OF_STAY_TOOLTIP_PITSTOP}>
                            <TextField
                                label = {LENGTH_OF_STAY_PITSTOP}
                                fullWidth
                                inputMode = "numeric"
                                onChange = {setNumericValue}
                                defaultValue = {defaultValuePitStop}
                                error = {shouldDisableSave}
                                helperText = {invalidInputMessage}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title = {LENGTH_OF_STAY_TOOLTIP}>
                            <TextField
                                label = {LENGTH_OF_STAY_OVERNIGHT}
                                fullWidth
                                inputMode = "numeric"
                                onChange = {setNumericValue}
                                defaultValue = {defaultValueOvernightStay}
                                error = {shouldDisableSave}
                                helperText = {invalidInputMessage}
                            />
                        </Tooltip>
                    )}
                    <Box
                        sx = {{
                            display: "flex",
                            gap: "16px",
                            paddingTop: "16px",
                        }}
                    >
                        <Button
                            onClick = {saveAndClose}
                            disabled = {shouldDisableSave}
                            variant = "contained"
                            sx = {{
                                flex: 1,
                                height: "48px",
                            }}
                        >
                            SAVE
                        </Button>
                        <Button
                            onClick = {cancelAndClose}
                            variant = "outlined"
                            sx = {{
                                flex: 1,
                                height: "48px",
                            }}
                        >
                            {DELETE_CONFIRMATION_NO}
                        </Button>
                    </Box>
                </DialogContent>
            )}
        </Dialog>
    );
}
